/**
 * Admin Server Functions
 *
 * This module provides TanStack Start server functions for admin-related
 * API calls. These functions wrap Orval-generated API functions to provide
 * server-side execution with automatic cookie forwarding via the custom instance.
 */

import { createServerFn } from "@tanstack/react-start";
import type {
  AdminCampaignListResponse,
  AdminListServiceRequestsApiAdminServiceRequestsGetParams,
  AdminServiceRequestAssetSignatureRequest,
  AdminServiceRequestPatchRequest,
  AdminServiceRequestUpdateCreateRequest,
  AdminWorkspaceCampaignBusinessUpdateRequest,
  AdminWorkspaceCampaignDetailResponse,
  AdminWorkspaceCampaignIcpUpdateRequest,
  AdminWorkspaceCampaignListResponse,
  AdminWorkspaceDetailResponse,
  AdminWorkspaceListResponse,
  AdminGlobalEmailListResponse,
  AdminGlobalEmailItemResponse,
  CampaignSettingResponse,
  ListAdminCampaignsApiAdminCampaignsGetParams,
  ListAdminWorkspaceCampaignsApiAdminWorkspacesWorkspaceIdCampaignsGetParams,
  ListWaitlistEntriesApiAdminWaitlistGetParams,
  ListAdminWorkspacesApiAdminWorkspacesGetParams,
  ListAdminAllEmailsApiAdminWorkspacesEmailsGetParams,
  ScheduleConfig,
  ScheduleWindowResponse,
  SenderAccountWithChannel,
  SequenceStepRequest,
  WaitlistApprovalRequest,
  OnboardingStatusResponse,
} from "@/lib/api/generated";
import {
  getAdminWorkspaceCampaignDetailApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdGet,
  getAdminWorkspaceDetailApiAdminWorkspacesWorkspaceIdGet,
  listAdminCampaignsApiAdminCampaignsGet,
  listAdminWorkspaceCampaignsApiAdminWorkspacesWorkspaceIdCampaignsGet,
  listWaitlistEntriesApiAdminWaitlistGet,
  listAdminWorkspacesApiAdminWorkspacesGet,
  listAdminAllEmailsApiAdminWorkspacesEmailsGet,
  updateAdminWorkspaceCampaignBusinessApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdBusinessPatch,
  updateAdminWorkspaceCampaignIcpApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdIcpPatch,
  approveWaitlistEntryApiAdminWaitlistWaitlistIdApprovePatch,
  adminCreateServiceAssetSignatureApiAdminServiceRequestsRequestIdAssetsSignaturePost,
  adminCreateServiceRequestUpdateApiAdminServiceRequestsRequestIdTimelinePost,
  adminGetServiceRequestApiAdminServiceRequestsRequestIdGet,
  adminListServiceRequestsApiAdminServiceRequestsGet,
  adminListServiceRequestTimelineApiAdminServiceRequestsRequestIdTimelineGet,
  adminUpdateServiceRequestApiAdminServiceRequestsRequestIdPatch,
} from "@/lib/api/generated/admin/admin";
import { logInfo, logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";
import { customInstance } from "@/lib/api/custom-instance";

function createDefaultScheduleConfig(): ScheduleConfig {
  return {
    days: {
      monday: { enabled: true, start_time: "09:00", end_time: "18:00" },
      tuesday: { enabled: true, start_time: "09:00", end_time: "18:00" },
      wednesday: { enabled: true, start_time: "09:00", end_time: "18:00" },
      thursday: { enabled: true, start_time: "09:00", end_time: "18:00" },
      friday: { enabled: true, start_time: "09:00", end_time: "18:00" },
      saturday: { enabled: false, start_time: "09:00", end_time: "18:00" },
      sunday: { enabled: false, start_time: "09:00", end_time: "18:00" },
    },
  };
}

function normalizeScheduleTime(value: string | null | undefined, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  const parts = trimmed.split(":");
  if (parts.length >= 2) {
    const hours = parts[0]?.trim();
    const minutes = parts[1]?.trim();
    if (hours && minutes) {
      return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
    }
  }
  return fallback;
}

function mapScheduleWindowsToScheduleConfig(
  scheduleWindows: ScheduleWindowResponse[] | null | undefined,
): ScheduleConfig {
  const base = createDefaultScheduleConfig();
  const mapNumToCode = (n: number): keyof ScheduleConfig["days"] => {
    const map: Record<number, keyof ScheduleConfig["days"]> = {
      1: "monday",
      2: "tuesday",
      3: "wednesday",
      4: "thursday",
      5: "friday",
      6: "saturday",
      7: "sunday",
      0: "sunday",
    };
    return map[n] ?? "monday";
  };

  (scheduleWindows ?? []).forEach((w) => {
    const day = mapNumToCode(w.weekday);
    const fallback = base.days[day];
    base.days[day] = {
      enabled: w.enabled,
      start_time: normalizeScheduleTime(w.start_time, fallback.start_time),
      end_time: normalizeScheduleTime(w.end_time, fallback.end_time),
    };
  });

  return base;
}

export interface AdminInvitationCodeDailyRegistration {
  date: string;
  count: number;
}

export interface AdminInvitationCodeListItem {
  id: string;
  code: string;
  created_at: string;
  workspace_id: string | null;
  workspace_slug: string | null;
  workspace_name: string | null;
  register_count: number;
  activated_count: number;
  non_activated_count: number;
  activation_rate: number;
  daily_registrations: AdminInvitationCodeDailyRegistration[];
}

export interface AdminInvitationCodeListResponse {
  items: AdminInvitationCodeListItem[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Get waitlist entries (admin only)
 *
 * Server function that retrieves waitlist entries with optional filtering and pagination.
 * Requires system_admin role.
 */
export const getWaitlistEntriesServer = createServerFn({ method: "GET" })
  .validator((data: ListWaitlistEntriesApiAdminWaitlistGetParams) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_waitlist_entries.server_fn_called", {
      skip: data.skip,
      limit: data.limit,
      status: data.status,
    });

    const response = await listWaitlistEntriesApiAdminWaitlistGet(data);

    if (response.status !== 200) {
      logError("admin.get_waitlist_entries.failed", {
        status: response.status,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to get waitlist entries",
          status: response.status,
        }),
      );
    }

    // The API now returns { items, total, skip, limit }
    // Return as unknown to bypass TanStack Start's type inference issues with Orval generated types
    return response.data as unknown;
  });

/**
 * Approve waitlist entry (admin only)
 *
 * Server function that approves a waitlist entry by ID.
 * Updates the entry status and the user's onboarding state.
 * Requires system_admin role.
 */
export const approveWaitlistServer = createServerFn({ method: "POST" })
  .validator((data: { waitlistId: string; reviewComment?: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.approve_waitlist.server_fn_called", {
      waitlist_id: data.waitlistId,
      has_comment: !!data.reviewComment,
    });

    const payload: WaitlistApprovalRequest = {
      review_comment: data.reviewComment || null,
    };

    const response = await approveWaitlistEntryApiAdminWaitlistWaitlistIdApprovePatch(
      data.waitlistId,
      payload,
    );

    if (response.status !== 200) {
      logError("admin.approve_waitlist.failed", {
        status: response.status,
        waitlist_id: data.waitlistId,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to approve waitlist entry",
          status: response.status,
        }),
      );
    }

    logInfo("admin.approve_waitlist.success", {
      waitlist_id: data.waitlistId,
    });

    return response.data as OnboardingStatusResponse;
  });

export const getAdminServiceRequestsServer = createServerFn({ method: "GET" })
  .validator((data: AdminListServiceRequestsApiAdminServiceRequestsGetParams) => data)
  .handler(async ({ data }) => {
    logInfo("admin.service_requests.list.server_fn_called", {
      skip: data.skip,
      limit: data.limit,
      status: data.status,
      service_type: data.service_type,
    });
    const response = await adminListServiceRequestsApiAdminServiceRequestsGet(data);
    if (response.status !== 200) {
      logError("admin.service_requests.list.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to list service requests"));
    }
    return response.data as unknown;
  });

export const getAdminServiceRequestServer = createServerFn({ method: "GET" })
  .validator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const response = await adminGetServiceRequestApiAdminServiceRequestsRequestIdGet(
      data.requestId,
    );
    if (response.status !== 200) {
      logError("admin.service_requests.get.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to get service request"));
    }
    return response.data as unknown;
  });

export const updateAdminServiceRequestServer = createServerFn({ method: "POST" })
  .validator((data: { requestId: string; payload: AdminServiceRequestPatchRequest }) => data)
  .handler(async ({ data }) => {
    const response = await adminUpdateServiceRequestApiAdminServiceRequestsRequestIdPatch(
      data.requestId,
      data.payload,
    );
    if (response.status !== 200) {
      logError("admin.service_requests.update.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to update service request"));
    }
    return response.data as unknown;
  });

export const getAdminServiceRequestTimelineServer = createServerFn({ method: "GET" })
  .validator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const response =
      await adminListServiceRequestTimelineApiAdminServiceRequestsRequestIdTimelineGet(
        data.requestId,
      );
    if (response.status !== 200) {
      logError("admin.service_requests.timeline.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to get service timeline"));
    }
    return response.data as unknown;
  });

export const createAdminServiceRequestUpdateServer = createServerFn({ method: "POST" })
  .validator((data: { requestId: string; payload: AdminServiceRequestUpdateCreateRequest }) => data)
  .handler(async ({ data }) => {
    const response =
      await adminCreateServiceRequestUpdateApiAdminServiceRequestsRequestIdTimelinePost(
        data.requestId,
        data.payload,
      );
    if (response.status !== 201) {
      logError("admin.service_requests.timeline.create_failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to publish service update"));
    }
    return response.data as unknown;
  });

export const createAdminServiceAssetSignatureServer = createServerFn({ method: "POST" })
  .validator(
    (data: { requestId: string; payload: AdminServiceRequestAssetSignatureRequest }) => data,
  )
  .handler(async ({ data }) => {
    const response =
      await adminCreateServiceAssetSignatureApiAdminServiceRequestsRequestIdAssetsSignaturePost(
        data.requestId,
        data.payload,
      );
    if (response.status !== 201) {
      logError("admin.service_requests.asset_signature.create_failed", {
        status: response.status,
      });
      throw new Error(extractApiErrorMessage(response.data, "Failed to create upload signature"));
    }
    return response.data as unknown;
  });

/**
 * List workspaces (admin only)
 *
 * Server function that retrieves workspaces with pagination and optional search.
 * Requires system_admin role.
 */
export const getAdminWorkspacesServer = createServerFn({ method: "GET" })
  .validator((data: ListAdminWorkspacesApiAdminWorkspacesGetParams) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_workspaces.server_fn_called", {
      skip: data.skip,
      limit: data.limit,
      query: data.query ?? null,
    });

    const response = await listAdminWorkspacesApiAdminWorkspacesGet(data);

    if (response.status !== 200) {
      logError("admin.get_workspaces.failed", {
        status: response.status,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list workspaces",
          status: response.status,
        }),
      );
    }

    return response.data as AdminWorkspaceListResponse;
  });

/**
 * Get workspace detail (admin only)
 *
 * Server function that retrieves aggregated workspace detail.
 * Requires system_admin role.
 */
export const getAdminWorkspaceDetailServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_workspace_detail.server_fn_called", {
      workspace_id: data.workspaceId,
    });

    const response = await getAdminWorkspaceDetailApiAdminWorkspacesWorkspaceIdGet(
      data.workspaceId,
    );

    if (response.status !== 200) {
      logError("admin.get_workspace_detail.failed", {
        status: response.status,
        workspace_id: data.workspaceId,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to get workspace detail",
          status: response.status,
        }),
      );
    }

    return response.data as AdminWorkspaceDetailResponse;
  });

/**
 * List workspace campaigns (admin only)
 *
 * Server function that retrieves campaigns for a workspace with pagination.
 * Requires system_admin role.
 */
export const getAdminWorkspaceCampaignsServer = createServerFn({ method: "GET" })
  .validator(
    (
      data: ListAdminWorkspaceCampaignsApiAdminWorkspacesWorkspaceIdCampaignsGetParams & {
        workspaceId: string;
      },
    ) => data,
  )
  .handler(async ({ data }) => {
    logInfo("admin.get_workspace_campaigns.server_fn_called", {
      workspace_id: data.workspaceId,
      skip: data.skip ?? 0,
      limit: data.limit ?? 20,
    });

    const response = await listAdminWorkspaceCampaignsApiAdminWorkspacesWorkspaceIdCampaignsGet(
      data.workspaceId,
      {
        skip: data.skip,
        limit: data.limit,
      },
    );

    if (response.status !== 200) {
      logError("admin.get_workspace_campaigns.failed", {
        status: response.status,
        workspace_id: data.workspaceId,
        skip: data.skip ?? 0,
        limit: data.limit ?? 20,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list workspace campaigns",
          status: response.status,
        }),
      );
    }

    return response.data as AdminWorkspaceCampaignListResponse;
  });

/**
 * Get workspace campaign detail (admin only)
 *
 * Server function that retrieves a single campaign within a workspace.
 * Requires system_admin role.
 */
export const getAdminWorkspaceCampaignDetailServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_workspace_campaign_detail.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    const response =
      await getAdminWorkspaceCampaignDetailApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdGet(
        data.workspaceId,
        data.campaignId,
      );

    if (response.status !== 200) {
      logError("admin.get_workspace_campaign_detail.failed", {
        status: response.status,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to get workspace campaign detail",
          status: response.status,
        }),
      );
    }

    return response.data as AdminWorkspaceCampaignDetailResponse;
  });

/**
 * List admin campaigns (admin only)
 *
 * Server function that retrieves all campaigns with running statistics.
 * Requires system_admin role.
 */
export const getAdminCampaignsServer = createServerFn({ method: "GET" })
  .validator((data: ListAdminCampaignsApiAdminCampaignsGetParams) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_campaigns.server_fn_called", {
      skip: data.skip,
      limit: data.limit,
      status: data.status,
      query: data.query,
    });

    const response = await listAdminCampaignsApiAdminCampaignsGet(data);

    if (response.status !== 200) {
      logError("admin.get_campaigns.failed", {
        status: response.status,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list campaigns",
          status: response.status,
        }),
      );
    }

    logInfo("admin.get_campaigns.success", {
      count: response.data.items.length,
      total: response.data.total,
    });

    return response.data as AdminCampaignListResponse;
  });

/**
 * List invitation codes (admin only)
 */
export const listAdminInvitationCodesServer = createServerFn({ method: "GET" })
  .validator((data: { skip?: number; limit?: number; query?: string }) => data)
  .handler(async ({ data }) => {
    const skip = data.skip ?? 0;
    const limit = data.limit ?? 20;
    const params = new URLSearchParams();
    params.set("skip", String(skip));
    params.set("limit", String(limit));
    if (data.query) {
      params.set("query", data.query);
    }

    logInfo("admin.list_invitation_codes.server_fn_called", {
      skip,
      limit,
      query: data.query ?? null,
    });

    try {
      const response = await customInstance<{
        data: AdminInvitationCodeListResponse;
        status: number;
      }>(`/api/admin/invitation-codes?${params.toString()}`, {
        method: "GET",
      });
      if (response.status !== 200) {
        throw new Error(
          JSON.stringify({
            detail: "Failed to list invitation codes",
            status: response.status,
          }),
        );
      }

      logInfo("admin.list_invitation_codes.success", {
        count: response.data.items.length,
        total: response.data.total,
      });
      return response.data;
    } catch (error) {
      logError("admin.list_invitation_codes.failed", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list invitation codes",
          status: 500,
        }),
      );
    }
  });

/**
 * List workspace emails (admin only)
 *
 * Server function that retrieves email messages for a workspace with pagination.
 * Requires system_admin role.
 */
export interface AdminWorkspaceEmailItem {
  message_id: string;
  thread_id: string;
  campaign_id: string | null;
  deal_id: string | null;
  contact_id: string | null;
  direction: "outbound" | "inbound";
  sender_email: string;
  sender_display_name: string;
  recipient_emails: string[];
  subject: string;
  body_preview: string;
  body: string;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
}

export interface AdminWorkspaceEmailListResponse {
  items: AdminWorkspaceEmailItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface AdminCampaignHealthTargetStatus {
  status: string;
  count: number;
}

export interface AdminCampaignHealthSequence {
  sequence_step_count: number;
  channels: string[];
  missing_reasons: string[];
}

export interface AdminCampaignHealthSender {
  sender_info_present: boolean;
  sender_name_present: boolean;
  sender_job_title_present: boolean;
  configured_channel_counts: Record<string, number>;
  active_email_account_count: number;
  connected_integration_counts: Record<string, number>;
  missing_reasons: string[];
}

export interface AdminCampaignHealthIcp {
  confirmed_icp_count: number;
  confirmed_search_queries: string[];
  missing_reasons: string[];
}

export interface AdminCampaignHealthRuntimeEntities {
  target_company_count: number;
  target_company_by_status: AdminCampaignHealthTargetStatus[];
  deal_count: number;
  outbox_count: number;
  deal_created_outbox_status_counts: Record<string, number>;
  deals_without_company: number;
  deals_without_contacts: number;
  deals_with_required_channel_contact: number;
  deals_missing_required_channel_contact: number;
  draft_count: number;
  send_task_count: number;
  first_sequence_channel: string | null;
}

export interface AdminCampaignHealthCheckResponse {
  campaign_id: string;
  workspace_id: string;
  name: string | null;
  status: string;
  agent_mode: string;
  ready: boolean;
  sequence: AdminCampaignHealthSequence;
  sender: AdminCampaignHealthSender;
  icp: AdminCampaignHealthIcp;
  runtime_entities: AdminCampaignHealthRuntimeEntities;
  missing_categories: string[];
  direct_reasons: string[];
  runtime_facts: Record<string, object>;
}

export interface AdminRealtimeJob {
  job_id: string;
  workspace_id: string;
  campaign_id: string;
  owner_type: string | null;
  owner_id: string | null;
  icp_id: string | null;
  realtime_query_id: string | null;
  provider: string | null;
  search_query: string;
  country_code: string | null;
  status: string;
  current_stage: string | null;
  submitted_at: string | null;
  last_webhook_at: string | null;
  finished_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminRealtimeWebhookEvent {
  id: string;
  event_key: string;
  status: string;
  current_stage: string | null;
  result_companies_count: number | null;
  top_level_companies_count: number | null;
  emitted_at: string | null;
  finished_at: string | null;
  processed_at: string;
  created_at: string;
}

export interface AdminRealtimeQuery {
  id: string;
  campaign_id: string;
  icp_id: string;
  keyword: string;
  query_text: string;
  country_code: string;
  status: string;
  job_id: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminRealtimeCampaign {
  campaign_id: string;
  workspace_id: string;
  workspace_slug: string | null;
  campaign_name: string | null;
  campaign_status: string;
}

export interface AdminRealtimeStatusCount {
  candidate_kind: string;
  status: string;
  count: number;
  first_added_at: string | null;
  last_added_at: string | null;
  last_updated_at: string | null;
}

export interface AdminRealtimeDealSummary {
  total_deals: number;
  recent_deals_after_webhook: number;
  first_deal_at: string | null;
  last_deal_at: string | null;
}

export interface AdminRealtimeJobDiagnosticResponse {
  found: boolean;
  consumption_status: "not_found" | "consumed" | "failed" | "finished_without_event" | "processing";
  summary: string;
  job: AdminRealtimeJob | null;
  campaign: AdminRealtimeCampaign | null;
  query: AdminRealtimeQuery | null;
  events: AdminRealtimeWebhookEvent[];
  target_counts: AdminRealtimeStatusCount[];
  source_icp_target_counts: AdminRealtimeStatusCount[];
  deal_summary: AdminRealtimeDealSummary | null;
}

export const getAdminWorkspaceEmailsServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      skip?: number;
      limit?: number;
      direction?: "outbound" | "inbound";
      campaignId?: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const skip = data.skip ?? 0;
    const limit = data.limit ?? 20;
    const params = new URLSearchParams();
    params.set("skip", String(skip));
    params.set("limit", String(limit));
    if (data.direction) {
      params.set("direction", data.direction);
    }
    if (data.campaignId) {
      params.set("campaign_id", data.campaignId);
    }

    logInfo("admin.get_workspace_emails.server_fn_called", {
      workspace_id: data.workspaceId,
      skip,
      limit,
      direction: data.direction ?? null,
    });

    try {
      const response = await customInstance<{
        data: AdminWorkspaceEmailListResponse;
        status: number;
      }>(`/api/admin/workspaces/${data.workspaceId}/emails?${params.toString()}`, {
        method: "GET",
      });
      if (response.status !== 200) {
        logError("admin.get_workspace_emails.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list workspace emails",
            status: response.status,
          }),
        );
      }

      logInfo("admin.get_workspace_emails.success", {
        count: response.data.items.length,
        total: response.data.total,
        workspace_id: data.workspaceId,
      });
      return response.data;
    } catch (error) {
      logError("admin.get_workspace_emails.failed", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list workspace emails",
          status: 500,
        }),
      );
    }
  });

export const getAdminWorkspaceCampaignHealthCheckServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<AdminCampaignHealthCheckResponse> => {
    logInfo("admin.get_workspace_campaign_health_check.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    const response = await customInstance<{
      data: AdminCampaignHealthCheckResponse;
      status: number;
    }>(`/api/admin/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/health-check`, {
      method: "GET",
    });

    const status = response.status as number;
    if (status !== 200) {
      const fallbackMessage = "Failed to get campaign health check";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.get_workspace_campaign_health_check.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }

    return response.data as AdminCampaignHealthCheckResponse;
  });

export const getAdminRealtimeJobDiagnosticServer = createServerFn({ method: "GET" })
  .validator((data: { jobId: string }) => data)
  .handler(async ({ data }): Promise<AdminRealtimeJobDiagnosticResponse> => {
    const jobId = data.jobId.trim();
    logInfo("admin.get_realtime_job_diagnostic.server_fn_called", {
      job_id: jobId,
    });

    const response = await customInstance<{
      data: AdminRealtimeJobDiagnosticResponse;
      status: number;
    }>(`/api/admin/monitor/realtime-jobs/${encodeURIComponent(jobId)}`, {
      method: "GET",
    });

    const status = response.status as number;
    if (status !== 200) {
      const fallbackMessage = "Failed to get realtime job diagnostic";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.get_realtime_job_diagnostic.failed", {
        job_id: jobId,
        status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }

    return response.data;
  });

/**
 * Update workspace campaign business (admin only)
 */
export const updateAdminWorkspaceCampaignBusinessServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      payload: AdminWorkspaceCampaignBusinessUpdateRequest;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("admin.update_workspace_campaign_business.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      selling_points: data.payload.selling_points?.length ?? 0,
      products: data.payload.products?.length ?? 0,
      has_website: Boolean(data.payload.website),
    });

    const response =
      await updateAdminWorkspaceCampaignBusinessApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdBusinessPatch(
        data.workspaceId,
        data.campaignId,
        data.payload,
      );

    if (response.status !== 200) {
      const fallbackMessage = "Failed to update campaign business";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.update_workspace_campaign_business.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status: response.status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.update_workspace_campaign_business.success", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    return response.data as AdminWorkspaceCampaignDetailResponse;
  });

/**
 * Update workspace campaign ICP (admin only)
 */
export const updateAdminWorkspaceCampaignIcpServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      payload: AdminWorkspaceCampaignIcpUpdateRequest;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("admin.update_workspace_campaign_icp.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      source: data.payload.source,
    });

    const response =
      await updateAdminWorkspaceCampaignIcpApiAdminWorkspacesWorkspaceIdCampaignsCampaignIdIcpPatch(
        data.workspaceId,
        data.campaignId,
        data.payload,
      );

    if (response.status !== 200) {
      const fallbackMessage = "Failed to update campaign ICP";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.update_workspace_campaign_icp.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status: response.status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.update_workspace_campaign_icp.success", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    return response.data as AdminWorkspaceCampaignDetailResponse;
  });

export const getAdminWorkspaceCampaignSettingServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<CampaignSettingResponse | null> => {
    logInfo("admin.get_workspace_campaign_setting.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    const response = await customInstance<{
      data: unknown;
      status: number;
    }>(`/api/admin/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/setting`, {
      method: "GET",
    });

    const status = response.status as number;
    if (status === 404) {
      return null;
    }

    if (status !== 200) {
      const fallbackMessage = "Failed to get campaign setting";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.get_workspace_campaign_setting.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }

    return response.data as CampaignSettingResponse;
  });

export const updateAdminWorkspaceCampaignSequenceServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; campaignId: string; sequence: SequenceStepRequest[] }) => data,
  )
  .handler(async ({ data }): Promise<CampaignSettingResponse> => {
    logInfo("admin.update_workspace_campaign_sequence.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      step_count: data.sequence.length,
    });

    const current = await customInstance<{
      data: unknown;
      status: number;
    }>(`/api/admin/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/setting`, {
      method: "GET",
    });

    const currentStatus = current.status as number;
    if (currentStatus === 404) {
      throw new Error(
        JSON.stringify({
          detail: "Campaign setting not found",
          status: 404,
        }),
      );
    }

    if (currentStatus !== 200) {
      const fallbackMessage = "Failed to load campaign setting";
      const errorMessage = extractApiErrorMessage(current, fallbackMessage);
      logError("admin.update_workspace_campaign_sequence.load_setting_failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status: currentStatus,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: currentStatus,
        }),
      );
    }

    const setting = current.data as CampaignSettingResponse;
    const senderName = setting.sender_name?.trim() ?? "";
    const senderJobTitle = setting.sender_job_title?.trim() ?? "";
    const senderAccounts: SenderAccountWithChannel[] = (setting.sender_accounts ?? []).map(
      (account) => ({
        account_id: account.account_id,
        channel: account.channel,
      }),
    );

    if (!senderName || !senderJobTitle || senderAccounts.length === 0) {
      throw new Error(
        JSON.stringify({
          detail:
            "Campaign setting is incomplete (sender name/job title/sender accounts are required).",
          status: 422,
        }),
      );
    }

    const normalizedSequence: SequenceStepRequest[] = data.sequence.map((step) => ({
      ...step,
      channel: "email",
    }));

    const response = await customInstance<{
      data: unknown;
      status: number;
    }>(`/api/admin/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/setting`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sender_name: senderName,
        sender_job_title: senderJobTitle,
        sender_accounts: senderAccounts,
        sequence: normalizedSequence,
        knowledge_attachment_ids: setting.knowledge_attachment_ids ?? [],
        language: setting.language,
        timezone: setting.timezone,
        schedule: mapScheduleWindowsToScheduleConfig(setting.schedule_windows),
      }),
    });

    const status = response.status as number;
    if (status !== 201 && status !== 200) {
      const fallbackMessage = "Failed to update campaign sequence";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.update_workspace_campaign_sequence.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }

    return response.data as CampaignSettingResponse;
  });

import {
  listAdminsApiAdminAdminsGet,
  addAdminApiAdminAdminsPost,
  removeAdminApiAdminAdminsUserIdDelete,
  checkAdminAccessApiAdminAdminsMeCheckGet,
} from "@/lib/api/generated/admin/admin";
import type {
  AddAdminRequest,
  RemoveAdminApiAdminAdminsUserIdDeleteBody,
} from "@/lib/api/generated";

/**
 * List system admins (admin only)
 *
 * Server function that retrieves all system administrators.
 * Requires system_admin role.
 */
export const listAdminsServer = createServerFn({ method: "GET" }).handler(async () => {
  logInfo("admin.list_admins.server_fn_called");

  const response = await listAdminsApiAdminAdminsGet();

  if (response.status !== 200) {
    logError("admin.list_admins.failed", {
      status: response.status,
    });
    throw new Error(
      JSON.stringify({
        detail: "Failed to list system admins",
        status: response.status,
      }),
    );
  }

  logInfo("admin.list_admins.success", {
    count: response.data.total,
  });

  return response.data;
});

/**
 * Add system admin (admin only)
 *
 * Server function that grants system admin privileges to a user.
 * Requires system_admin role.
 */
export const addAdminServer = createServerFn({ method: "POST" })
  .validator((data: AddAdminRequest) => data)
  .handler(async ({ data }) => {
    logInfo("admin.add_admin.server_fn_called", {
      email: data.email,
    });

    const response = await addAdminApiAdminAdminsPost(data);

    if (response.status !== 201) {
      const fallbackMessage = "Failed to add system admin";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.add_admin.failed", {
        status: response.status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.add_admin.success", {
      user_id: response.data.user?.id,
    });

    return response.data;
  });

/**
 * Remove system admin (admin only)
 *
 * Server function that revokes system admin privileges from a user.
 * Requires system_admin role.
 */
export const removeAdminServer = createServerFn({ method: "POST" })
  .validator((data: { userId: string; force?: boolean }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.remove_admin.server_fn_called", {
      user_id: data.userId,
      force: data.force,
    });

    const body: RemoveAdminApiAdminAdminsUserIdDeleteBody = {
      force: data.force,
    };
    const response = await removeAdminApiAdminAdminsUserIdDelete(data.userId, body);

    if (response.status !== 200) {
      const fallbackMessage = "Failed to remove system admin";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.remove_admin.failed", {
        user_id: data.userId,
        status: response.status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.remove_admin.success", {
      user_id: data.userId,
    });

    return response.data;
  });

/**
 * Check if current user is system admin
 *
 * Server function that checks if the authenticated user has system admin privileges.
 */
export const checkAdminAccessServer = createServerFn({ method: "GET" }).handler(async () => {
  logInfo("admin.check_access.server_fn_called");

  const response = await checkAdminAccessApiAdminAdminsMeCheckGet();

  if (response.status !== 200) {
    logError("admin.check_access.failed", {
      status: response.status,
    });
    throw new Error(
      JSON.stringify({
        detail: "Failed to check admin access",
        status: response.status,
      }),
    );
  }

  logInfo("admin.check_access.success", {
    is_admin: response.data.is_admin,
  });

  return response.data;
});

export interface AdminMonitorCampaignItem {
  campaign_id: string;
  campaign_name: string | null;
  workspace_id: string;
  workspace_slug: string;
  status: "normal" | "abnormal" | string;
  new_target_company_count: number;
  new_pending_target_company_count: number;
  new_contacted_target_company_count: number;
  new_rejected_target_company_count: number;
  new_opportunities_count: number;
  new_outbound_count: number;
  is_workspace_eligible: boolean;
  reason: string | null;
  query_workflow_status: string | null;
  schedule_workflow_status: string | null;
  updated_at: string | null;
}

export interface AdminMonitorSummary {
  eligible_workspace_count: number;
  active_campaign_count: number;
  normal_campaign_count: number;
  abnormal_campaign_count: number;
  total_new_target_company_count: number;
  total_new_opportunities_count: number;
  total_new_outbound_count: number;
}

export interface AdminMonitorResponse {
  generated_at: string;
  temporal_available: boolean;
  summary: AdminMonitorSummary;
  campaigns: AdminMonitorCampaignItem[];
}

export interface AdminMonitorRealtimeQueryItem {
  keyword: string;
  country_code: string;
  planned_for_at: string;
  status: string;
  job_id: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface AdminMonitorRealtimeQueryListResponse {
  campaign_id: string;
  items: AdminMonitorRealtimeQueryItem[];
}

export const getAdminMonitorCampaignsServer = createServerFn({ method: "GET" }).handler(
  async () => {
    logInfo("admin.get_monitor_campaigns.server_fn_called");

    const response = await customInstance<{
      data: AdminMonitorResponse;
      status: number;
    }>("/api/admin/monitor/campaigns", {
      method: "GET",
    });

    if (response.status !== 200) {
      const fallbackMessage = "Failed to get admin monitor campaigns";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.get_monitor_campaigns.failed", {
        status: response.status,
        error: errorMessage,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.get_monitor_campaigns.success", {
      campaign_count: response.data.campaigns.length,
    });

    return response.data;
  },
);

export const getAdminMonitorCampaignRealtimeQueriesServer = createServerFn({ method: "GET" })
  .validator((data: { campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_monitor_campaign_realtime_queries.server_fn_called", {
      campaign_id: data.campaignId,
    });

    const response = await customInstance<{
      data: AdminMonitorRealtimeQueryListResponse;
      status: number;
    }>(`/api/admin/monitor/campaigns/${data.campaignId}/realtime-queries`, {
      method: "GET",
    });

    if (response.status !== 200) {
      const fallbackMessage = "Failed to get admin monitor campaign realtime queries";
      const errorMessage = extractApiErrorMessage(response, fallbackMessage);
      logError("admin.get_monitor_campaign_realtime_queries.failed", {
        status: response.status,
        error: errorMessage,
        campaign_id: data.campaignId,
      });
      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status: response.status,
        }),
      );
    }

    logInfo("admin.get_monitor_campaign_realtime_queries.success", {
      campaign_id: data.campaignId,
      query_count: response.data.items.length,
    });

    return response.data;
  });

// ============================================================================
// 试用期管理 Server Functions
// ============================================================================

export interface TrialStatusResponse {
  status: string;
  trial_start: string | null;
  trial_end: string | null;
  days_remaining: number | null;
  can_extend: boolean;
  stripe_subscription_id: string | null;
  plan_type: string | null;
}

export interface TrialSetResponse {
  success: boolean;
  workspace_id: string;
  new_trial_end: string;
  previous_trial_end: string | null;
  operation: string;
  stripe_subscription_id: string | null;
}

/**
 * Get workspace trial status (admin only)
 */
export const getWorkspaceTrialStatusServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.get_trial_status.server_fn_called", {
      workspace_id: data.workspaceId,
    });

    try {
      const response = await customInstance<{
        data: TrialStatusResponse;
        status: number;
      }>(`/api/admin/workspaces/${data.workspaceId}/trial`, {
        method: "GET",
      });

      if (response.status !== 200) {
        logError("admin.get_trial_status.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get trial status",
            status: response.status,
          }),
        );
      }

      logInfo("admin.get_trial_status.success", {
        workspace_id: data.workspaceId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      logError("admin.get_trial_status.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * Set/extend workspace trial (admin only)
 */
export const setWorkspaceTrialServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; days: number; reason?: string }) => data)
  .handler(async ({ data }) => {
    logInfo("admin.set_trial.server_fn_called", {
      workspace_id: data.workspaceId,
      days: data.days,
      has_reason: !!data.reason,
    });

    try {
      const response = await customInstance<{
        data: TrialSetResponse;
        status: number;
      }>(`/api/admin/workspaces/${data.workspaceId}/trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          days: data.days,
          reason: data.reason,
        }),
      });

      if (response.status !== 200) {
        const fallbackMessage = "Failed to set trial";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);
        logError("admin.set_trial.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          error: errorMessage,
        });
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("admin.set_trial.success", {
        workspace_id: data.workspaceId,
        operation: response.data.operation,
        new_trial_end: response.data.new_trial_end,
      });

      return response.data;
    } catch (error) {
      logError("admin.set_trial.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

export type { AdminGlobalEmailListResponse, AdminGlobalEmailItemResponse };

/**
 * List all emails across all workspaces (global admin inbox)
 *
 * Server function that retrieves emails across all workspaces.
 * Requires system_admin role.
 */
export const getAdminAllEmailsServer = createServerFn({ method: "GET" })
  .validator((data: ListAdminAllEmailsApiAdminWorkspacesEmailsGetParams) => data)
  .handler(async ({ data }) => {
    const requestParams: ListAdminAllEmailsApiAdminWorkspacesEmailsGetParams = {
      ...data,
      direction: data.direction ?? undefined,
      workspace_id: data.workspace_id ?? undefined,
    };

    logInfo("admin.get_all_emails.server_fn_called", {
      skip: data.skip,
      limit: data.limit,
      direction: data.direction ?? null,
      workspace_id: data.workspace_id ?? null,
    });

    const response = await listAdminAllEmailsApiAdminWorkspacesEmailsGet(requestParams);

    if (response.status !== 200) {
      logError("admin.get_all_emails.failed", {
        status: response.status,
      });
      throw new Error(
        JSON.stringify({
          detail: "Failed to list all emails",
          status: response.status,
        }),
      );
    }

    return response.data as AdminGlobalEmailListResponse;
  });
